import React, { useEffect, useState, useMemo } from "react";
import { Users, Search, Send, ChevronDown, ChevronRight, CheckCircle2, Clock, GraduationCap, BookOpen, Calendar, ClipboardList, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const ITEM_ICONS = {
  training_registration: ClipboardList,
  retreat_registration: Calendar,
  booking: BookOpen,
  enrollment: GraduationCap,
};

const ITEM_LABELS = {
  training_registration: "Training registration",
  retreat_registration: "Retreat registration",
  booking: "Class booking",
  enrollment: "LMS enrollment",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

/**
 * Admin → Users tab.
 *
 * Lists every account with engagement counts and an inline "send password
 * reset" action so Holly can resolve "I can't log in" emails in two clicks
 * without ever opening MongoDB.
 *
 * Each row expands to show the user's full activity timeline.
 */
export default function AdminUsersTab() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resettingId, setResettingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load users.");
      setUsers([]);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!users) return null;
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const toggleExpand = async (user) => {
    if (expandedId === user.id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(user.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}`);
      setDetail(data);
    } catch (e) {
      toast.error("Couldn't load user detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  const sendReset = async (user) => {
    if (!window.confirm(`Send a password-reset link to ${user.email}? They'll receive it from noreply@avianayoga.com and the link expires in 30 minutes.`)) return;
    setResettingId(user.id);
    try {
      await api.post(`/admin/users/${user.id}/send-password-reset`);
      toast.success(`Reset link sent to ${user.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send reset link.");
    } finally {
      setResettingId(null);
    }
  };

  const revokeEnrollment = async (user, enrollment) => {
    const courseName = enrollment.title || "this course";
    if (!window.confirm(`Revoke ${user.email}'s access to "${courseName}"? They'll immediately lose access to all lessons inside. You can re-grant it later by manually re-enrolling them.`)) return;
    try {
      await api.delete(`/admin/users/${user.id}/enrollments/${enrollment.id}`);
      toast.success(`Access to "${courseName}" revoked for ${user.email}.`);
      // Refresh both the row counts and the expanded detail
      await load();
      const { data } = await api.get(`/admin/users/${user.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't revoke access.");
    }
  };

  const grantExtension = async (user, enrollment) => {
    const monthsStr = window.prompt("How many months to extend? (1, 2, or 3)");
    if (!monthsStr) return;
    const months = parseInt(monthsStr, 10);
    if (![1, 2, 3].includes(months)) {
      toast.error("Please enter 1, 2, or 3.");
      return;
    }
    const paidVia = window.prompt("How was it paid? (zelle, venmo, comp, other)", "zelle");
    if (!paidVia) return;
    const amountStr = window.prompt("Amount paid in USD (leave blank for default: 1mo=$100, 2mo=$300, 3mo=$500)");
    const body = { months, paid_via: paidVia.toLowerCase().trim() };
    if (amountStr && !isNaN(parseInt(amountStr, 10))) body.amount_usd = parseInt(amountStr, 10);
    try {
      await api.post(`/admin/enrollments/${enrollment.id}/extend`, body);
      toast.success(`Granted ${months}-month extension to ${user.email}.`);
      await load();
      const { data } = await api.get(`/admin/users/${user.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't grant extension.");
    }
  };

  if (users === null) {
    return <p className="text-aviana-muted">Loading users…</p>;
  }

  const totals = {
    all: users.length,
    activated: users.filter((u) => u.activated).length,
    unactivated: users.filter((u) => !u.activated).length,
    modules: users.filter((u) => u.has_modules_access).length,
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Users size={20} className="text-aviana-plum" />
          <h2 className="font-display text-3xl text-aviana-ink">Users</h2>
        </div>
        <p className="text-aviana-muted text-[14px]">Every account, what they've registered for, and one-click password resets.</p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard label="Total" value={totals.all} />
        <StatCard label="Activated" value={totals.activated} tone="ok" />
        <StatCard label="Awaiting first sign-in" value={totals.unactivated} tone="warn" />
        <StatCard label="Modules access" value={totals.modules} />
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-aviana-muted" />
        <input
          type="text"
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white border border-aviana-border rounded-full text-[14px] focus:border-aviana-gold outline-none"
          data-testid="users-search"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-aviana-muted py-12">No users match that search.</p>
      ) : (
        <div className="space-y-2" data-testid="users-list">
          {filtered.map((u) => {
            const expanded = expandedId === u.id;
            return (
              <div
                key={u.id}
                className={`bg-white border rounded-2xl overflow-hidden transition-colors ${expanded ? "border-aviana-plum/30" : "border-aviana-border"}`}
                data-testid={`user-row-${u.id}`}
              >
                <div className="flex items-center gap-4 p-5">
                  <button
                    onClick={() => toggleExpand(u)}
                    className="text-aviana-muted hover:text-aviana-ink p-1"
                    aria-label={expanded ? "Collapse" : "Expand"}
                    data-testid={`expand-${u.id}`}
                  >
                    {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium text-aviana-ink text-[15px]">{u.name || u.email.split("@")[0]}</span>
                      <span className="text-aviana-muted text-[13px]">{u.email}</span>
                      {u.role === "admin" && (
                        <span className="text-[10px] tracking-[0.18em] uppercase text-aviana-plum bg-aviana-plum/10 px-2 py-0.5 rounded-full">Admin</span>
                      )}
                      {u.activated ? (
                        <span className="text-[10px] tracking-[0.18em] uppercase text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title="Has signed in / changed password">
                          <CheckCircle2 size={10} /> Activated
                        </span>
                      ) : (
                        <span className="text-[10px] tracking-[0.18em] uppercase text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title="Hasn't yet set their own password">
                          <Clock size={10} /> Awaiting first sign-in
                        </span>
                      )}
                      {u.has_modules_access && (
                        <span className="text-[10px] tracking-[0.18em] uppercase text-aviana-goldDeep bg-aviana-gold/15 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <GraduationCap size={10} /> Modules
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 text-[12px] text-aviana-muted flex gap-4 flex-wrap">
                      <span>Joined {fmtDate(u.created_at)}</span>
                      {u.training_registrations > 0 && <span>{u.training_registrations} training{u.training_registrations === 1 ? "" : "s"}</span>}
                      {u.retreat_registrations > 0 && <span>{u.retreat_registrations} retreat{u.retreat_registrations === 1 ? "" : "s"}</span>}
                      {u.bookings > 0 && <span>{u.bookings} booking{u.bookings === 1 ? "" : "s"}</span>}
                      {u.enrollments > 0 && <span>{u.enrollments} enrolment{u.enrollments === 1 ? "" : "s"}</span>}
                    </div>
                  </div>

                  <button
                    onClick={() => sendReset(u)}
                    disabled={resettingId === u.id}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aviana-plum text-aviana-cream text-[11px] tracking-[0.18em] uppercase hover:bg-aviana-plumDeep disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    data-testid={`send-reset-${u.id}`}
                  >
                    <Send size={12} /> {resettingId === u.id ? "Sending…" : "Send reset link"}
                  </button>
                </div>

                {expanded && (
                  <div className="border-t border-aviana-border bg-aviana-paper/50 px-5 py-5" data-testid={`user-detail-${u.id}`}>
                    {detailLoading ? (
                      <p className="text-aviana-muted text-[13px]">Loading activity…</p>
                    ) : !detail ? (
                      <p className="text-aviana-muted text-[13px] italic">No activity yet.</p>
                    ) : (
                      <>
                      {detail.items.length === 0 ? (
                        <p className="text-aviana-muted text-[13px] italic mb-4">No registrations, bookings, or enrollments yet.</p>
                      ) : (
                      <div className="space-y-2">
                        <p className="overline mb-3">Activity timeline</p>
                        {detail.items.map((item, i) => {
                          const Icon = ITEM_ICONS[item.kind] || ClipboardList;
                          const canRevoke = item.kind === "enrollment" && item.id;
                          return (
                            <div key={i} className="flex items-start gap-3 py-2.5 px-3 bg-white rounded-xl border border-aviana-border/60">
                              <div className="w-8 h-8 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
                                <Icon size={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                  <span className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted">{ITEM_LABELS[item.kind] || item.kind}</span>
                                  {item.status && (
                                    <span className="text-[10px] text-aviana-plum bg-aviana-plum/8 px-1.5 py-0.5 rounded">{item.status}</span>
                                  )}
                                </div>
                                <p className="text-aviana-ink text-[14px] leading-[1.5] mt-0.5">{item.title || "—"}</p>
                                {item.extra?.start_date && (
                                  <p className="text-aviana-muted text-[12px] mt-0.5">Event date: {fmtDate(item.extra.start_date)}</p>
                                )}
                                {item.extra?.studio_pays_tuition && (
                                  <p className="text-aviana-goldDeep text-[12px] mt-0.5">Tuition paid by studio</p>
                                )}
                                {item.extra?.amount_today_usd != null && (
                                  <p className="text-aviana-muted text-[12px] mt-0.5">
                                    ${item.extra.amount_today_usd?.toLocaleString()} paid · ${item.extra.balance_due_usd?.toLocaleString() || 0} balance
                                  </p>
                                )}
                                {item.kind === "enrollment" && (
                                  <div className="mt-2 space-y-1">
                                    {item.extra?.access_expires_at ? (
                                      <p className="text-[12px] text-aviana-ink" data-testid={`enrollment-expiry-${item.id}`}>
                                        <strong>Access expires:</strong> {fmtDate(item.extra.access_expires_at)}
                                      </p>
                                    ) : (
                                      <p className="text-[12px] text-aviana-muted italic">Lifetime access (no expiry)</p>
                                    )}
                                    <p className="text-[12px] text-aviana-plum" data-testid={`enrollment-ext-count-${item.id}`}>
                                      <strong>{item.extra?.extensions_used ?? 0} of 2</strong> extensions used
                                      {(item.extra?.extensions_used ?? 0) > 0 && (
                                        <span className="text-aviana-muted"> · {item.extra?.extensions_remaining ?? 0} remaining</span>
                                      )}
                                    </p>
                                    {item.extra?.extensions_history?.length > 0 && (
                                      <ul className="text-[11px] text-aviana-muted list-disc ml-4 space-y-0.5">
                                        {item.extra.extensions_history.map((ext, idx) => (
                                          <li key={idx}>
                                            +{ext.months || "?"}mo · {ext.paid_via || "—"} · ${ext.amount_usd ?? "—"} · {fmtDate(ext.granted_at)}
                                            {ext.note ? ` · "${ext.note}"` : ""}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                    {item.extra?.reminders_sent?.length > 0 && (
                                      <p className="text-[11px] text-aviana-goldDeep" data-testid={`enrollment-reminders-${item.id}`}>
                                        <strong>Reminders sent:</strong> {item.extra.reminders_sent.join(" · ")}
                                        {item.extra.last_reminder_at && (
                                          <span className="text-aviana-muted"> (last {fmtDate(item.extra.last_reminder_at)})</span>
                                        )}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1.5">
                                <span className="text-aviana-muted text-[11px] whitespace-nowrap">{fmtDate(item.created_at)}</span>
                                {canRevoke && (
                                  <div className="flex flex-col items-end gap-1">
                                    {(item.extra?.extensions_used ?? 0) < 2 ? (
                                      <button
                                        onClick={() => grantExtension(u, item)}
                                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-aviana-goldDeep hover:bg-aviana-gold/10 px-2 py-1 rounded-md transition-colors"
                                        data-testid={`extend-enrollment-${item.id}`}
                                        title="Manually grant an extension (Zelle/Venmo/comp)"
                                      >
                                        <Sparkles size={11} /> Grant extension
                                      </button>
                                    ) : (
                                      <span
                                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-aviana-muted px-2 py-1 rounded-md"
                                        title="Maximum of 2 extensions already granted"
                                        data-testid={`extension-maxed-${item.id}`}
                                      >
                                        Max extensions reached
                                      </span>
                                    )}
                                    <button
                                      onClick={() => revokeEnrollment(u, item)}
                                      className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-red-700/80 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded-md transition-colors"
                                      data-testid={`revoke-enrollment-${item.id}`}
                                      title="Revoke this course access"
                                    >
                                      <Trash2 size={11} /> Revoke access
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                      <LmsDrillDown detail={detail} />
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneClass =
    tone === "ok" ? "text-emerald-700" :
    tone === "warn" ? "text-amber-700" :
    "text-aviana-ink";
  return (
    <div className="bg-white border border-aviana-border rounded-2xl p-5">
      <p className="overline text-aviana-muted">{label}</p>
      <p className={`font-figure text-3xl mt-1 ${toneClass}`}>{value}</p>
    </div>
  );
}

// ─── LMS drill-down: hours, self-evals, student-evals, module submissions ───
// Rendered inside each expanded user row so Holly can inspect the actual
// records — not just counts. Each category collapses by default and shows a
// summary chip; click to expand and see the full entries.
function LmsDrillDown({ detail }) {
  const hours = detail?.lms_hours || { items: [], total_hours: 0, count: 0 };
  const selfEvals = detail?.lms_self_evals || [];
  const studentEvals = detail?.lms_student_evals || [];
  const submissions = detail?.lms_submissions || [];
  const hasAny = hours.count > 0 || selfEvals.length > 0 || studentEvals.length > 0 || submissions.length > 0;
  if (!hasAny) return null;
  return (
    <div className="mt-6 pt-6 border-t border-aviana-border/60">
      <p className="overline mb-3">Training module records</p>
      <div className="grid gap-3">
        {hours.count > 0 && (
          <DrillSection
            testid={`drill-hours-${detail.user.id}`}
            title="Practice Teaching Hours"
            subtitle={`${hours.total_hours}h logged across ${hours.count} session${hours.count === 1 ? "" : "s"}`}
          >
            <div className="space-y-2 mt-3">
              {hours.items.map((h) => (
                <div key={h.id} className="flex items-center gap-3 py-2 px-3 bg-white rounded-lg border border-aviana-border/60 text-[13px]">
                  <span className="font-display text-lg text-aviana-gold min-w-[52px]">{h.duration_minutes}m</span>
                  <div className="flex-1">
                    <p className="text-aviana-ink">{h.date} · {h.location || "—"} · {h.student_count} student{h.student_count === 1 ? "" : "s"}</p>
                    {h.notes && <p className="text-aviana-muted text-[12px] italic mt-0.5">"{h.notes}"</p>}
                  </div>
                </div>
              ))}
            </div>
          </DrillSection>
        )}
        {selfEvals.length > 0 && (
          <DrillSection
            testid={`drill-self-evals-${detail.user.id}`}
            title="Self-Evaluations"
            subtitle={`${selfEvals.length} submitted`}
          >
            <div className="space-y-3 mt-3">
              {selfEvals.map((s) => (
                <div key={s.id} className="py-3 px-4 bg-white rounded-lg border border-aviana-border/60 text-[13px]">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted mb-2">Submitted {fmtDate(s.submitted_at)}</p>
                  {Array.isArray(s.answers) && s.answers.length > 0 ? (
                    <ul className="space-y-2">
                      {s.answers.map((a, i) => (
                        <li key={i}>
                          <p className="text-aviana-ink font-medium">{a.question || a.q || `Question ${i + 1}`}</p>
                          <p className="text-aviana-inkSoft whitespace-pre-wrap mt-1">{a.answer || a.a || "—"}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <pre className="text-[12px] text-aviana-muted whitespace-pre-wrap">{JSON.stringify(s, null, 2)}</pre>
                  )}
                </div>
              ))}
            </div>
          </DrillSection>
        )}
        {studentEvals.length > 0 && (
          <DrillSection
            testid={`drill-student-evals-${detail.user.id}`}
            title="Student Evaluations Received"
            subtitle={`${studentEvals.length} student${studentEvals.length === 1 ? "" : "s"} rated this instructor`}
          >
            <div className="space-y-3 mt-3">
              {studentEvals.map((e) => (
                <div key={e.id} className="py-3 px-4 bg-white rounded-lg border border-aviana-border/60 text-[13px]">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1.5">
                    <p className="text-aviana-ink">
                      <strong>{e.student_name || "Anonymous student"}</strong>
                      {e.student_email && <span className="text-aviana-muted ml-2">&lt;{e.student_email}&gt;</span>}
                    </p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted">{fmtDate(e.submitted_at)}</p>
                  </div>
                  {e.rating != null && (
                    <p className="text-aviana-goldDeep text-[13px]">Rating: <strong>{e.rating}/5</strong></p>
                  )}
                  {e.comments && <p className="text-aviana-inkSoft whitespace-pre-wrap mt-1 italic">"{e.comments}"</p>}
                  {Array.isArray(e.answers) && (
                    <ul className="space-y-1 mt-2">
                      {e.answers.map((a, i) => (
                        <li key={i} className="text-[12px]">
                          <span className="text-aviana-muted">{a.question || a.q || `Q${i + 1}`}: </span>
                          <span className="text-aviana-ink">{a.answer || a.a || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </DrillSection>
        )}
        {submissions.length > 0 && (
          <DrillSection
            testid={`drill-submissions-${detail.user.id}`}
            title="Module Submissions"
            subtitle={`${submissions.length} submitted · ${submissions.filter(s => s.status === "submitted").length} awaiting review · ${submissions.filter(s => s.status === "approved").length} approved`}
          >
            <div className="space-y-3 mt-3">
              {submissions.map((s) => (
                <div key={s.id} className="py-3 px-4 bg-white rounded-lg border border-aviana-border/60 text-[13px]" data-testid={`submission-${s.id}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1.5">
                    <p className="text-aviana-ink">
                      <strong>{(s.module_key || "submission").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</strong>
                      <span className={`ml-2 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${
                        s.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                        s.status === "needs_revision" ? "bg-amber-100 text-amber-800" :
                        s.status === "rejected" ? "bg-red-100 text-red-700" :
                        "bg-aviana-plum/15 text-aviana-plum"
                      }`}>{s.status || "submitted"}</span>
                    </p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-aviana-muted">{fmtDate(s.submitted_at)}</p>
                  </div>
                  {s.external_url && (
                    <p className="text-[12px]"><a href={s.external_url} target="_blank" rel="noreferrer" className="text-aviana-plum underline break-all">{s.external_url}</a></p>
                  )}
                  {s.file_url && (
                    <p className="text-[12px]"><a href={s.file_url} target="_blank" rel="noreferrer" className="text-aviana-plum underline break-all">Attached file</a></p>
                  )}
                  {s.text_content && (
                    <p className="text-aviana-inkSoft whitespace-pre-wrap mt-1.5">{s.text_content.length > 500 ? s.text_content.slice(0, 500) + "…" : s.text_content}</p>
                  )}
                  {s.reviewer_notes && (
                    <p className="text-[12px] text-aviana-goldDeep mt-1.5 border-l-2 border-aviana-gold pl-2 italic">Holly's note: {s.reviewer_notes}</p>
                  )}
                </div>
              ))}
            </div>
          </DrillSection>
        )}
      </div>
    </div>
  );
}

function DrillSection({ title, subtitle, testid, children }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-aviana-border/60 rounded-xl bg-aviana-paper/40 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 py-3 px-4 hover:bg-white/60 transition-colors text-left"
      >
        <div>
          <p className="text-aviana-ink font-medium text-[13px]">{title}</p>
          <p className="text-aviana-muted text-[12px] mt-0.5">{subtitle}</p>
        </div>
        <span className="text-aviana-muted text-[11px] tracking-[0.18em] uppercase whitespace-nowrap">{open ? "Hide ×" : "View ↓"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">{children}</div>
      )}
    </div>
  );
}
